import { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../api/client';
import { formatUzs } from '../lib/format';
import { format, parseISO } from 'date-fns';
import { Modal } from '../components/Modal';
import { toast } from '../stores/toasts';

const DAY_LABELS_UZ = ['Yak', 'Du', 'Se', 'Ch', 'Pa', 'Ju', 'Sh'];

export function WorkshopDetailPage() {
  const { id } = useParams<{ id: string }>();
  const nav = useNavigate();
  const [bookingOpen, setBookingOpen] = useState(false);

  const { data: workshop, isLoading } = useQuery({
    queryKey: ['marketplace-workshop', id],
    queryFn: () => api.getWorkshop(id!),
    enabled: !!id,
  });

  const { data: services } = useQuery({
    queryKey: ['workshop-services', id],
    queryFn: () => api.getWorkshopServices(id!),
    enabled: !!id,
  });

  if (isLoading) return <div className="spinner" />;
  if (!workshop) return <div className="empty">Servis topilmadi</div>;

  const workingHours = workshop.workingHours as Record<string, { open: string; close: string } | null> | null;
  const days = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];

  return (
    <div>
      <button className="btn btn-ghost btn-sm" onClick={() => nav(-1)} style={{ marginBottom: 10 }}>
        ← Orqaga
      </button>

      <div className="card">
        <div className="row-between" style={{ marginBottom: 8 }}>
          <div style={{ fontSize: 22, fontWeight: 700 }}>{workshop.name}</div>
          {workshop.isVerified && <span className="tag success">✓ Tasdiqlangan</span>}
        </div>
        {workshop.description && (
          <div className="muted" style={{ marginBottom: 8 }}>{workshop.description}</div>
        )}
        <div className="muted" style={{ fontSize: 13 }}>
          📍 {workshop.address ?? workshop.city ?? '—'}
        </div>
        {workshop.phone && (
          <div className="muted" style={{ fontSize: 13 }}>
            📞 <a href={`tel:${workshop.phone}`}>{workshop.phone}</a>
          </div>
        )}
        {workshop.rating > 0 && (
          <div style={{ marginTop: 8 }}>
            <span className="tag warning">⭐ {workshop.rating.toFixed(1)} · {workshop.reviewsCount} ta sharh</span>
          </div>
        )}
      </div>

      <div className="card">
        <div className="field-label" style={{ marginBottom: 8 }}>🕐 Ish vaqti</div>
        {workingHours ? (
          days.map((d) => {
            const h = workingHours[d];
            const dayIdx = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'].indexOf(d);
            return (
              <div key={d} className="row-between" style={{ padding: '4px 0' }}>
                <div>{DAY_LABELS_UZ[dayIdx]}</div>
                <div className="muted" style={{ fontSize: 13 }}>
                  {h ? `${h.open} – ${h.close}` : 'Yopiq'}
                </div>
              </div>
            );
          })
        ) : (
          <div className="muted" style={{ fontSize: 13 }}>Ish vaqti ko'rsatilmagan</div>
        )}
      </div>

      {services && services.length > 0 && (
        <div className="card">
          <div className="field-label" style={{ marginBottom: 8 }}>🔧 Xizmatlar</div>
          {services.map((s: any) => (
            <div key={s.id} className="row-between" style={{ padding: '6px 0', borderBottom: '1px solid var(--border)' }}>
              <div>
                <div style={{ fontWeight: 500 }}>{s.name}</div>
                {s.description && <div className="muted" style={{ fontSize: 12 }}>{s.description}</div>}
              </div>
              <div className="amount" style={{ fontWeight: 600, color: 'var(--primary)' }}>
                {s.priceFrom ? `${formatUzs(Number(s.priceFrom))}+` : '—'}
              </div>
            </div>
          ))}
        </div>
      )}

      {workshop.reviews && workshop.reviews.length > 0 && (
        <div className="card">
          <div className="field-label" style={{ marginBottom: 8 }}>⭐ Sharhlar</div>
          {workshop.reviews.slice(0, 5).map((r: any) => (
            <div key={r.id} style={{ paddingBottom: 10, marginBottom: 10, borderBottom: '1px solid var(--border)' }}>
              <div className="row-between" style={{ marginBottom: 4 }}>
                <div style={{ fontWeight: 500 }}>{r.client?.name ?? 'Mijoz'}</div>
                <span className="amount">{'⭐'.repeat(r.rating)}</span>
              </div>
              {r.text && <div style={{ fontSize: 13 }}>{r.text}</div>}
              <div className="muted" style={{ fontSize: 11, marginTop: 4 }}>{format(parseISO(r.createdAt), 'dd MMM yyyy')}</div>
            </div>
          ))}
        </div>
      )}

      <button className="btn btn-primary btn-block" style={{ marginTop: 16 }} onClick={() => setBookingOpen(true)}>
        📅 Band qilish
      </button>

      {bookingOpen && <BookingFlow workshopId={workshop.id} onClose={() => setBookingOpen(false)} />}
    </div>
  );
}

interface BookingFlowProps {
  workshopId: string;
  onClose: () => void;
}

function BookingFlow({ workshopId, onClose }: BookingFlowProps) {
  const nav = useNavigate();
  const [step, setStep] = useState<'service' | 'car' | 'date' | 'time' | 'note' | 'review'>('service');
  const [serviceId, setServiceId] = useState<string>('');
  const [carId, setCarId] = useState<string>('');
  const [date, setDate] = useState<string>('');
  const [time, setTime] = useState<string>('');
  const [note, setNote] = useState('');

  const { data: services } = useQuery({
    queryKey: ['workshop-services', workshopId],
    queryFn: () => api.getWorkshopServices(workshopId),
  });

  const { data: cars } = useQuery({
    queryKey: ['my-cars'],
    queryFn: () => api.myCars(),
  });

  const { data: slots } = useQuery({
    queryKey: ['workshop-slots', workshopId],
    queryFn: () => api.getWorkshopSlots(workshopId),
  });

  const [submitting, setSubmitting] = useState(false);

  async function submit() {
    setSubmitting(true);
    try {
      await api.createBooking({
        workshopId,
        serviceId,
        carId,
        scheduledAt: time,
        customerNote: note || undefined,
      });
      toast.success('Band qilindi! Servis tasdiqlashini kuting');
      onClose();
      nav('/my-bookings');
    } catch (e: any) {
      toast.error(e?.message || 'Xatolik yuz berdi');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={`Band qilish · ${step === 'service' ? 'Xizmat' : step === 'car' ? 'Mashina' : step === 'date' ? 'Sana' : step === 'time' ? 'Vaqt' : 'Tasdiqlash'}`}
      footer={
        <>
          {step !== 'service' && (
            <button className="btn" onClick={() => {
              const idx = ['service', 'car', 'date', 'time', 'review'].indexOf(step);
              if (idx > 0) setStep(['service', 'car', 'date', 'time', 'review'][idx - 1] as any);
            }}>← Orqaga</button>
          )}
          {step === 'review' ? (
            <button
              className="btn btn-primary"
              onClick={submit}
              disabled={submitting}
            >
              {submitting ? 'Yuborilmoqda...' : '✓ Tasdiqlash'}
            </button>
          ) : (
            <button
              className="btn btn-primary"
              disabled={
                (step === 'service' && !serviceId) ||
                (step === 'car' && !carId) ||
                (step === 'date' && !date) ||
                (step === 'time' && !time)
              }
              onClick={() => {
                const idx = ['service', 'car', 'date', 'time', 'review'].indexOf(step);
                if (idx < 4) setStep(['service', 'car', 'date', 'time', 'review'][idx + 1] as any);
              }}
            >
              Keyingi →
            </button>
          )}
        </>
      }
    >
      {step === 'service' && (
        <div className="col">
          <div className="muted" style={{ fontSize: 13, marginBottom: 8 }}>Xizmat turini tanlang</div>
          {(services ?? []).map((s: any) => (
            <div
              key={s.id}
              className="card"
              onClick={() => setServiceId(s.id)}
              style={{
                cursor: 'pointer',
                borderColor: serviceId === s.id ? 'var(--primary)' : undefined,
                background: serviceId === s.id ? 'rgba(36, 129, 204, 0.1)' : undefined,
              }}
            >
              <div className="row-between">
                <div>
                  <div style={{ fontWeight: 600 }}>{s.name}</div>
                  {s.description && <div className="muted" style={{ fontSize: 12 }}>{s.description}</div>}
                </div>
                <div className="amount" style={{ fontWeight: 600 }}>
                  {s.priceFrom ? formatUzs(Number(s.priceFrom)) : '—'}
                </div>
              </div>
            </div>
          ))}
          {(!services || services.length === 0) && (
            <div className="empty">Xizmatlar mavjud emas</div>
          )}
        </div>
      )}

      {step === 'car' && (
        <div className="col">
          <div className="muted" style={{ fontSize: 13, marginBottom: 8 }}>Mashinani tanlang</div>
          {(cars ?? []).map((c: any) => (
            <div
              key={c.id}
              className="card"
              onClick={() => setCarId(c.id)}
              style={{
                cursor: 'pointer',
                borderColor: carId === c.id ? 'var(--primary)' : undefined,
                background: carId === c.id ? 'rgba(36, 129, 204, 0.1)' : undefined,
              }}
            >
              <div style={{ fontWeight: 600 }}>🚗 {c.plate}</div>
              <div className="muted" style={{ fontSize: 12 }}>{c.brand} {c.model}{c.year ? ` · ${c.year}` : ''}</div>
            </div>
          ))}
          {(!cars || cars.length === 0) && (
            <div className="empty">
              <div className="emoji">🚗</div>
              <div>Avval mashina qo'shing</div>
              <Link to="/my-cars" className="btn btn-primary" style={{ marginTop: 10 }} onClick={onClose}>
                ➕ Mashina qo'shish
              </Link>
            </div>
          )}
        </div>
      )}

      {step === 'date' && (
        <div className="col">
          <div className="muted" style={{ fontSize: 13, marginBottom: 8 }}>Sanani tanlang</div>
          {(slots ?? []).map((s) => {
            const d = parseISO(s.date);
            const hasSlots = s.times.length > 0;
            return (
              <div
                key={s.date}
                className="card"
                onClick={() => hasSlots && setDate(s.date)}
                style={{
                  cursor: hasSlots ? 'pointer' : 'not-allowed',
                  opacity: hasSlots ? 1 : 0.5,
                  borderColor: date === s.date ? 'var(--primary)' : undefined,
                  background: date === s.date ? 'rgba(36, 129, 204, 0.1)' : undefined,
                }}
              >
                <div className="row-between">
                  <div>
                    <div style={{ fontWeight: 600 }}>{format(d, 'dd MMM, EEEE', { locale: undefined })}</div>
                    <div className="muted" style={{ fontSize: 12 }}>{s.times.length} ta vaqt mavjud</div>
                  </div>
                  {!hasSlots && <span className="tag">Yopiq</span>}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {step === 'time' && (
        <div className="col">
          <div className="muted" style={{ fontSize: 13, marginBottom: 8 }}>Vaqtni tanlang</div>
          <div className="chips">
            {(slots?.find((s) => s.date === date)?.times ?? []).map((t) => (
              <div
                key={t}
                className={`chip ${time === t ? 'active' : ''}`}
                onClick={() => setTime(t)}
              >
                {format(parseISO(t), 'HH:mm')}
              </div>
            ))}
          </div>
          {(slots?.find((s) => s.date === date)?.times ?? []).length === 0 && (
            <div className="empty">Bu sanada bo'sh vaqt yo'q</div>
          )}
        </div>
      )}

      {step === 'review' && (
        <div>
          <div className="muted" style={{ marginBottom: 10, fontSize: 13 }}>Tafsilotlarni tekshiring:</div>
          <div className="card">
            <div className="row-between" style={{ marginBottom: 6 }}>
              <span className="muted">Xizmat</span>
              <b>{(services ?? []).find((s: any) => s.id === serviceId)?.name ?? '—'}</b>
            </div>
            <div className="row-between" style={{ marginBottom: 6 }}>
              <span className="muted">Mashina</span>
              <b>{(cars ?? []).find((c: any) => c.id === carId)?.plate ?? '—'}</b>
            </div>
            <div className="row-between" style={{ marginBottom: 6 }}>
              <span className="muted">Sana</span>
              <b>{date ? format(parseISO(date), 'dd MMM yyyy') : '—'}</b>
            </div>
            <div className="row-between" style={{ marginBottom: 6 }}>
              <span className="muted">Vaqt</span>
              <b>{time ? format(parseISO(time), 'HH:mm') : '—'}</b>
            </div>
          </div>
          <div className="field" style={{ marginTop: 10 }}>
            <label className="field-label">Izoh (ixtiyoriy)</label>
            <textarea
              rows={3}
              placeholder="Masalan: Moy almashtirish + filtr tekshirish"
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
          </div>
        </div>
      )}
    </Modal>
  );
}
